// Medication history for the patient portal (`/schedules/patient/meds`).
//
// Calendar — a month grid. Each day shows how many of that day's medicines
//            were ticked off (from `doses_log`), plus a marker on days where
//            the medication list itself changed. Tap a day for the detail.
//
// Everything here is derived from data the meds endpoints already return:
// `started_at`, `ended_at`, and `doses_log: [{date, taken_at, taken}]`. No new
// backend calls.
import React, { useEffect, useMemo, useRef, useState } from "react";

const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

// `started_at` is a full ISO timestamp, `doses_log[].date` is a bare
// YYYY-MM-DD. Both reduce to the same day key.
export function dayKey(value) {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function keyFromParts(year, monthIdx, day) {
  return `${year}-${String(monthIdx + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function todayKey() {
  const now = new Date();
  return keyFromParts(now.getFullYear(), now.getMonth(), now.getDate());
}

function prettyDate(key, opts) {
  if (!key) return "";
  const [y, m, d] = key.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(
    undefined,
    opts || { month: "short", day: "numeric", year: "numeric" }
  );
}

function daysBetween(fromKey, toKey) {
  if (!fromKey || !toKey) return null;
  const [y1, m1, d1] = fromKey.split("-").map(Number);
  const [y2, m2, d2] = toKey.split("-").map(Number);
  const ms = Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1);
  return Math.round(ms / 86400000);
}

export function prettyTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** Newest `{date, taken_at}` entry in a med's dose log, or null. */
export function lastDose(med) {
  const log = Array.isArray(med && med.dosesLog) ? med.dosesLog : [];
  let best = null;
  log.forEach((d) => {
    if (!d || d.taken === false) return;
    const key = dayKey(d.date);
    if (!key) return;
    if (!best || key > best.key) best = { key, at: d.taken_at || d.takenAt || null };
  });
  return best;
}

/** How many days a med was actually marked as taken. */
export function doseCount(med) {
  const log = Array.isArray(med && med.dosesLog) ? med.dosesLog : [];
  const days = new Set();
  log.forEach((d) => {
    if (!d || d.taken === false) return;
    const key = dayKey(d.date);
    if (key) days.add(key);
  });
  return days.size;
}

/** "Today at 8:04 AM" / "Mar 3 at 8:04 AM" — relative where it helps. */
export function describeDose(dose) {
  if (!dose) return "";
  const time = prettyTime(dose.at);
  const today = todayKey();
  const diff = daysBetween(dose.key, today);
  let when;
  if (dose.key === today) when = "Today";
  else if (diff === 1) when = "Yesterday";
  else when = prettyDate(dose.key);
  return time ? `${when} at ${time}` : when;
}

/** Human label for an editor record (`{name, kind, role}`).
 * Falls back to "Patient" / "Doctor" / "Care team" / "Scan" so the
 * history view never shows a blank "Added by …" header.
 */
export function describeEditor(editor, fallbackKind) {
  if (!editor) return describeEditor({ kind: fallbackKind }, fallbackKind);
  const kind = editor.kind || fallbackKind || "system";
  if (editor.name) {
    if (kind === "patient") return `${editor.name} (patient)`;
    if (kind === "scan") return `${editor.name} (scan)`;
    return editor.name;
  }
  if (kind === "doctor") return "Doctor";
  if (kind === "medstudent") return "Med student";
  if (kind === "admin") return "Admin";
  if (kind === "patient") return "Patient";
  if (kind === "scan") return "Scan";
  if (kind === "superadmin") return "Admin";
  return "Care team";
}

// Unit / form words we drop so "Lisinopril 20 mg tablet" and "lisinopril 20mg"
// collapse to the same drug, which is what lets us show a dose CHANGE instead
// of an unrelated stop + start pair.
const UNIT_WORDS = new Set([
  "mg", "mcg", "g", "ml", "l", "iu", "tab", "tabs", "tablet", "tablets",
  "capsule", "capsules", "cap", "caps", "puff", "puffs", "unit", "units",
  "spray", "sprays", "patch", "drop", "drops", "daily", "oral", "po",
]);

export function drugKey(name) {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .split(/\s+/)
    .filter((w) => w && !/\d/.test(w) && !UNIT_WORDS.has(w))
    .join(" ")
    .trim();
}

// ---------------------------------------------------------------------------
// Derivation
// ---------------------------------------------------------------------------

// A med counts toward a day if it had been started by then and hadn't been
// stopped yet. Meds saved before we tracked `started_at` have no bounds, so
// they count for every day (better than silently vanishing from the calendar).
//
// History behavior: each med has at most one `started_at` and one `ended_at`,
// so the day-DOSE picture is fully determined by those two timestamps plus
// the current value of the med's other fields (dose, reason, slot). When the
// list changes today — adding, stopping, or dose-changing — only FUTURE days
// shift to reflect the new state; past days keep the picture they always
// had because the bounds on past days don't change. This is what makes the
// calendar a stable record of what was on the list each day, not a live
// view that re-renders history whenever the list is edited.
function isActiveOn(med, key) {
  const started = dayKey(med.startedAt);
  const ended = dayKey(med.endedAt);
  if (started && key < started) return false;
  if (ended && key > ended) return false;
  return true;
}

function takenOn(med, key) {
  const log = Array.isArray(med.dosesLog) ? med.dosesLog : [];
  return log.find(
    (d) => d && dayKey(d.date) === key && d.taken !== false
  ) || null;
}

/** Per-day dose picture: which meds were due, which were ticked off. */
export function dayDoses(allMeds, key) {
  const due = allMeds.filter((m) => isActiveOn(m, key));
  const taken = [];
  const missed = [];
  due.forEach((m) => {
    const hit = takenOn(m, key);
    if (hit) taken.push({ med: m, at: hit.taken_at || hit.takenAt || null });
    else missed.push({ med: m });
  });
  return { due, taken, missed };
}

/** Field-level diff between two versions of the same drug. */
function diffFields(before, after) {
  const fields = [
    ["dose", "Dose"],
    ["reason", "Reason"],
    ["slot", "When"],
  ];
  const out = [];
  fields.forEach(([field, label]) => {
    const a = String((before && before[field]) || "").trim();
    const b = String((after && after[field]) || "").trim();
    if (a !== b) out.push({ label, from: a, to: b });
  });
  return out;
}

// How many days apart a stop and a re-start can be and still read as one
// "changed the dose" event rather than two unrelated events.
const RESTART_WINDOW_DAYS = 3;

/**
 * Build the change events for the timeline.
 *
 * Raw events are `added` (from `started_at`) and `stopped` (from `ended_at`).
 * A `stopped` immediately followed by an `added` of the SAME drug is folded
 * into a single `changed` event carrying the field-level diff — that's the
 * common case when a doctor adjusts a dose, or when the visit scan re-adds a
 * medicine at a new strength.
 */
export function buildChangeEvents(allMeds) {
  const raw = [];
  allMeds.forEach((med) => {
    const started = dayKey(med.startedAt);
    const ended = dayKey(med.endedAt);
    if (started) raw.push({ date: started, type: "added", med });
    if (ended) raw.push({ date: ended, type: "stopped", med });
  });
  raw.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const consumed = new Set();
  const events = [];
  raw.forEach((evt, i) => {
    if (consumed.has(i)) return;
    if (evt.type === "stopped") {
      const key = drugKey(evt.med.name);
      const restartIdx = raw.findIndex((other, j) => {
        if (j <= i || consumed.has(j)) return false;
        if (other.type !== "added") return false;
        if (drugKey(other.med.name) !== key) return false;
        const gap = daysBetween(evt.date, other.date);
        return gap !== null && gap >= 0 && gap <= RESTART_WINDOW_DAYS;
      });
      if (restartIdx !== -1) {
        const restart = raw[restartIdx];
        consumed.add(i);
        consumed.add(restartIdx);
        const changes = diffFields(evt.med, restart.med);
        events.push({
          date: restart.date,
          type: changes.length ? "changed" : "renewed",
          med: restart.med,
          previous: evt.med,
          changes,
        });
        return;
      }
    }
    consumed.add(i);
    events.push({
      date: evt.date,
      type: evt.type,
      med: evt.med,
      changes: [],
    });
  });

  events.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return events;
}

// ---------------------------------------------------------------------------
// Calendar view
// ---------------------------------------------------------------------------

const EVENT_META = {
  added: { icon: "+", label: "Started", cls: "is-added" },
  stopped: { icon: "−", label: "Stopped", cls: "is-stopped" },
  changed: { icon: "±", label: "Changed", cls: "is-changed" },
  renewed: { icon: "↻", label: "Re-added", cls: "is-renewed" },
};

function CalendarView({ allMeds, eventsByDate, selected, onSelect }) {
  const today = todayKey();
  // Earliest day any med on the list was started. The calendar can't be
  // scrolled before this — anything older is guaranteed to be empty (no
  // meds existed yet) and only adds noise. Falls back to today when the
  // list is empty, which is the same behavior as before.
  const earliestKey = useMemo(() => {
    let earliest = null;
    allMeds.forEach((m) => {
      const k = dayKey(m.startedAt);
      if (k && (!earliest || k < earliest)) earliest = k;
    });
    return earliest;
  }, [allMeds]);
  const earliestCursor = useMemo(() => {
    if (!earliestKey) return null;
    const [y, m] = earliestKey.split("-").map(Number);
    return { year: y, month: m - 1 };
  }, [earliestKey]);
  const [cursor, setCursor] = useState(() => {
    // Prefer the earliest med date over "today" so the calendar opens on
    // the first month that actually has data, not months of empty cells.
    // If `selected` already points to a different month (e.g. the user
    // clicked a day from the timeline), honor that instead.
    const seed = selected || earliestKey || today;
    const [y, m] = seed.split("-").map(Number);
    return { year: y, month: m - 1 };
  });

  // The selected date lives in the parent. When it changes externally — e.g.
  // the user opens a day from the Changes timeline — make sure the calendar
  // jumps to that month so the highlighted day is actually visible. Clicking
  // inside the calendar also fires onSelect, so we compare against the
  // previous key to avoid a redundant setState ping.
  const lastSyncedRef = useRef(selected);
  useEffect(() => {
    if (!selected || !lastSyncedRef.current) {
      lastSyncedRef.current = selected;
      return;
    }
    if (selected === lastSyncedRef.current) return;
    lastSyncedRef.current = selected;
    const [y, m] = selected.split("-").map(Number);
    setCursor((c) => (c.year === y && c.month === m - 1 ? c : { year: y, month: m - 1 }));
  }, [selected]);

  const cells = useMemo(() => {
    const first = new Date(cursor.year, cursor.month, 1);
    const daysInMonth = new Date(cursor.year, cursor.month + 1, 0).getDate();
    const lead = first.getDay();
    const out = [];
    for (let i = 0; i < lead; i += 1) out.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) {
      const key = keyFromParts(cursor.year, cursor.month, d);
      const { due, taken } = dayDoses(allMeds, key);
      // Days before the first med on the list are rendered as disabled
      // (greyed, not clickable) instead of empty pads so the user can see
      // they're real calendar days, just outside their tracking window.
      const preEarliest = earliestKey ? key < earliestKey : false;
      out.push({
        key,
        day: d,
        due: due.length,
        taken: taken.length,
        changes: (eventsByDate.get(key) || []).length,
        future: key > today,
        preEarliest,
      });
    }
    return out;
  }, [cursor, allMeds, eventsByDate, today, earliestKey]);

  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString(
    undefined,
    { month: "long", year: "numeric" }
  );
  const atEarliest = earliestCursor
    ? cursor.year === earliestCursor.year && cursor.month === earliestCursor.month
    : false;

  const step = (delta) => {
    setCursor((c) => {
      const d = new Date(c.year, c.month + delta, 1);
      const next = { year: d.getFullYear(), month: d.getMonth() };
      // Don't let the prev button walk past the earliest med date — any
      // earlier month is empty by construction (no meds existed yet).
      // Forward navigation is open: future months just show currently
      // active meds scheduled on each day.
      if (delta < 0 && earliestCursor) {
        if (
          next.year < earliestCursor.year ||
          (next.year === earliestCursor.year && next.month < earliestCursor.month)
        ) {
          return earliestCursor;
        }
      }
      return next;
    });
  };

  return (
    <div className="pp-medcal">
      <div className="pp-medcal-head">
        <button
          type="button"
          className="pp-medcal-nav"
          onClick={() => step(-1)}
          aria-label="Previous month"
          disabled={atEarliest}
        >
          ‹
        </button>
        <div className="pp-medcal-month">{monthLabel}</div>
        <button
          type="button"
          className="pp-medcal-nav"
          onClick={() => step(1)}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      <div className="pp-medcal-grid pp-medcal-dow">
        {WEEKDAYS.map((d, i) => (
          <div key={`${d}-${i}`} className="pp-medcal-dow-cell">{d}</div>
        ))}
      </div>

      <div className="pp-medcal-grid">
        {cells.map((cell, i) => {
          if (!cell) return <div key={`pad-${i}`} className="pp-medcal-pad" />;
          const complete = cell.due > 0 && cell.taken === cell.due;
          const partial = cell.taken > 0 && cell.taken < cell.due;
          const missed = !cell.future && cell.due > 0 && cell.taken === 0;
          const pct = cell.due ? (cell.taken / cell.due) * 100 : 0;
          // Lock down all past dates regardless of whether they have data —
          // the user asked to disable every past cell, not just the empty
          // ones. Future dates STAY clickable so you can preview what's
          // scheduled; past dates don't.
          const isPast = cell.key < today;
          const disabled = cell.preEarliest || isPast;
          return (
            <button
              key={cell.key}
              type="button"
              className={[
                "pp-medcal-day",
                cell.key === selected ? "is-selected" : "",
                cell.key === today ? "is-today" : "",
                cell.future ? "is-future" : "",
                isPast && !cell.preEarliest ? "is-past" : "",
                cell.preEarliest ? "is-preeearliest" : "",
                complete ? "is-complete" : "",
                partial ? "is-partial" : "",
                missed ? "is-missed" : "",
                cell.changes > 0 ? "is-changed" : "",
              ].filter(Boolean).join(" ")}
              onClick={() => {
                if (disabled) return;
                onSelect(cell.key);
              }}
              disabled={disabled}
              aria-label={cell.preEarliest
                ? `${prettyDate(cell.key)} — before you started tracking medicines`
                : isPast
                ? `${prettyDate(cell.key)} — past day`
                : cell.future
                ? `${prettyDate(cell.key)} — ${cell.due} medicine${cell.due === 1 ? "" : "s"} scheduled${
                    cell.changes ? `, ${cell.changes} list change${cell.changes === 1 ? "" : "s"}` : ""
                  }`
                : `${prettyDate(cell.key)} — ${cell.taken} of ${cell.due} taken${
                    cell.changes ? `, ${cell.changes} list change${cell.changes === 1 ? "" : "s"}` : ""
                  }`}
              title={
                cell.preEarliest
                  ? "Before you started tracking medicines"
                  : isPast
                  ? "Past day"
                  : cell.changes
                  ? `${prettyDate(cell.key)} — your medicine list changed here (${cell.changes})`
                  : undefined
              }
              aria-pressed={cell.key === selected}
            >
              <span className="pp-medcal-num">{cell.day}</span>
              {cell.due > 0 && !cell.preEarliest && (
                <span
                  className={`pp-medcal-meter ${cell.future ? "is-scheduled" : ""}`}
                  aria-hidden="true"
                >
                  <span
                    className="pp-medcal-meter-fill"
                    style={{ width: `${pct}%` }}
                  />
                </span>
              )}
              {cell.changes > 0 && (
                <>
                  <span className="pp-medcal-flag" aria-hidden="true">●</span>
                  {cell.changes > 1 && (
                    <span className="pp-medcal-flag-count" aria-hidden="true">
                      {cell.changes}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      <div className="pp-medcal-legend">
        <span><i className="pp-legend-dot is-complete" /> All taken</span>
        <span><i className="pp-legend-dot is-partial" /> Some taken</span>
        <span><i className="pp-legend-dot is-missed" /> None marked</span>
        <span><i className="pp-legend-dot is-flag" /> List changed</span>
      </div>
    </div>
  );
}

function ChangeRow({ event }) {
  const meta = EVENT_META[event.type] || EVENT_META.added;
  const med = event.med || {};
  const stoppedAfter =
    event.type === "stopped"
      ? daysBetween(dayKey(med.startedAt), dayKey(med.endedAt))
      : null;
  // For a stop, the useful fact is the LAST dose of the med being stopped —
  // for anything else it's the most recent dose of the med as it stands now.
  const doseSource = event.type === "stopped" ? med : (event.med || {});
  const last = lastDose(doseSource);
  const taken = doseCount(doseSource);
  // Editor identity comes from the med itself: `added_by_*` for added /
  // changed / renewed events, `discontinued_by_*` for stopped events.
  // `med.addedBy` / `med.discontinuedBy` are populated by the API mappers
  // (mapMed / mapCurrent) from the fields the backend stamps onto the
  // JSONB on every write; kinds are derived from public.users.role.
  const editor =
    event.type === "stopped"
      ? med.discontinuedBy
      : med.addedBy;
  const editorKind =
    event.type === "stopped"
      ? med.discontinuedBy && med.discontinuedBy.kind
      : med.addedBy && med.addedBy.kind;
  return (
    <div className={`pp-medchg ${meta.cls}`}>
      <span className="pp-medchg-icon" aria-hidden="true">{meta.icon}</span>
      <div className="pp-medchg-body">
        <div className="pp-medchg-top">
          <span className="pp-medchg-verb">{meta.label}</span>
          <span className="pp-medchg-name">{med.name}</span>
          {editor && (
            <span className="pp-medchg-by">
              <span className="pp-medchg-by-label">
                {event.type === "stopped" ? "Stopped by" : "Added by"}
              </span>
              <span className="pp-medchg-by-name">
                {describeEditor(editor, editorKind)}
              </span>
            </span>
          )}
        </div>
        {event.type === "changed" && (
          <ul className="pp-medchg-diff">
            {event.changes.map((c) => (
              <li key={c.label}>
                <span className="pp-medchg-field">{c.label}</span>
                <span className="pp-medchg-from">{c.from || "—"}</span>
                <span className="pp-medchg-arrow" aria-hidden="true">→</span>
                <span className="pp-medchg-to">{c.to || "—"}</span>
              </li>
            ))}
          </ul>
        )}
        {event.type !== "changed" && (med.dose || med.reason) && (
          <div className="pp-medchg-meta">
            {[med.dose, med.reason].filter(Boolean).join(" · ")}
          </div>
        )}
        {stoppedAfter !== null && stoppedAfter > 0 && (
          <div className="pp-medchg-meta">
            On the list for {stoppedAfter} day{stoppedAfter === 1 ? "" : "s"}
            {taken > 0 ? ` · marked taken on ${taken} of them` : " · never marked taken"}
          </div>
        )}
        {last && (
          <div className="pp-medchg-meta pp-medchg-last">
            <span aria-hidden="true">✓</span> Last taken {describeDose(last)}
          </div>
        )}
      </div>
    </div>
  );
}

function DayDetail({ allMeds, eventsByDate, dateKey, detailRef }) {
  const { due, taken, missed } = useMemo(
    () => dayDoses(allMeds, dateKey),
    [allMeds, dateKey]
  );
  const changes = eventsByDate.get(dateKey) || [];
  const isFuture = dateKey > todayKey();
  const today = todayKey();

  // Morning / evening slots for "my medicines on this day". A med is "Morning"
  // when its slot is morning (or anything else), Evening when explicitly
  // evening — that mirrors how PatientMeds splits the live list.
  const splitDue = useMemo(() => {
    const morning = [];
    const evening = [];
    due.forEach((m) => {
      const slot = m.slot === "evening" ? "evening" : "morning";
      const hit = taken.find((t) => t.med.id === m.id);
      if (slot === "evening") evening.push({ med: m, at: hit ? hit.at : null });
      else morning.push({ med: m, at: hit ? hit.at : null });
    });
    return { morning, evening };
  }, [due, taken]);

  return (
    <div className="pp-medday" ref={detailRef}>
      <div className="pp-medday-head">
        <div>
          <h3 className="pp-medday-title">{prettyDate(dateKey, {
            weekday: "long", month: "long", day: "numeric",
          })}</h3>
          <div className="pp-medday-eyebrow">
            {(() => {
              if (dateKey === today) return "Today";
              // `daysBetween(from, to)` returns `to - from`. Positive ⇒
              // `to` is after `from`.
              const delta = daysBetween(dateKey, today);
              if (delta === 1) return "Yesterday";
              if (delta > 1) return `${delta} days ago`;
              const ahead = -delta;
              return `In ${ahead} day${ahead === 1 ? "" : "s"}`;
            })()}
            {" · "}My medicines on this day
          </div>
        </div>
        {due.length > 0 && !isFuture && (
          <span className={`pp-row-pill ${taken.length === due.length ? "pp-green" : ""}`}>
            {taken.length} of {due.length} taken
          </span>
        )}
        {due.length > 0 && isFuture && (
          <span className="pp-row-pill pp-teal-pill">
            {due.length} scheduled
          </span>
        )}
      </div>

      {changes.length > 0 && (
        <div className="pp-medday-block">
          <div className="pp-medday-lbl">What changed</div>
          {changes.map((e, i) => (
            <ChangeRow key={`${e.type}-${e.med.id}-${i}`} event={e} />
          ))}
        </div>
      )}

      {due.length === 0 ? (
        <p className="pp-detail-p" style={{ marginTop: 8 }}>
          No medicines were on your list this day.
        </p>
      ) : (
        <div className="pp-medday-meds">
          {splitDue.morning.length > 0 && (
            <div className="pp-medday-block">
              <div className="pp-medday-lbl">Morning ({splitDue.morning.length})</div>
              {splitDue.morning.map(({ med, at }) => (
                <MedDayRow key={med.id} med={med} at={at} isFuture={isFuture} />
              ))}
            </div>
          )}
          {splitDue.evening.length > 0 && (
            <div className="pp-medday-block">
              <div className="pp-medday-lbl">Evening ({splitDue.evening.length})</div>
              {splitDue.evening.map(({ med, at }) => (
                <MedDayRow key={med.id} med={med} at={at} isFuture={isFuture} />
              ))}
            </div>
          )}
          {!isFuture && missed.length > 0 && (
            <div className="pp-medday-block">
              <div className="pp-medday-lbl">Not marked ({missed.length})</div>
              {missed.map(({ med }) => {
                const last = lastDose(med);
                return (
                  <div key={med.id} className="pp-medday-row">
                    <span className="pp-medday-tick is-empty" aria-hidden="true" />
                    <span className="pp-medday-name">{med.name}</span>
                    {last && (
                      <span className="pp-medday-last">
                        last taken {describeDose(last)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MedDayRow({ med, at, isFuture }) {
  const wasTaken = Boolean(at);
  return (
    <div className={`pp-medday-row ${wasTaken ? "is-taken" : ""} ${isFuture ? "is-future" : ""}`}>
      <span
        className={`pp-medday-tick ${wasTaken ? "" : "is-empty"}`}
        aria-hidden="true"
      >
        {wasTaken ? "✓" : ""}
      </span>
      <span className="pp-medday-name">{med.name}</span>
      {med.dose && (
        <span className="pp-medday-dose">{med.dose}</span>
      )}
      {wasTaken && at && <span className="pp-medday-time">{prettyTime(at)}</span>}
      {!wasTaken && isFuture && (
        <span className="pp-medday-last">scheduled</span>
      )}
      {!wasTaken && !isFuture && (
        <span className="pp-medday-last">not marked</span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export default function MedsHistory({ meds, history }) {
  const [selected, setSelected] = useState(todayKey);
  const detailRef = useRef(null);

  const allMeds = useMemo(() => {
    const active = (meds || []).map((m) => ({ ...m, endedAt: m.endedAt || null }));
    return [...active, ...(history || [])];
  }, [meds, history]);

  const events = useMemo(() => buildChangeEvents(allMeds), [allMeds]);
  const eventsByDate = useMemo(() => {
    const map = new Map();
    events.forEach((e) => {
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date).push(e);
    });
    return map;
  }, [events]);

  // Whenever the selected day changes, scroll the day's detail into view so
  // the user actually lands on the medicines for that date.
  useEffect(() => {
    const node = detailRef.current;
    if (!node) return;
    const t = setTimeout(() => {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      } catch (_) {
        node.scrollIntoView();
      }
    }, 0);
    return () => clearTimeout(t);
  }, [selected]);

  const changeDays = eventsByDate.size;
  const subtitle =
    allMeds.length === 0
      ? "Nothing to show yet."
      : `${allMeds.length} medicine${allMeds.length === 1 ? "" : "s"} tracked · ` +
        `${changeDays} day${changeDays === 1 ? "" : "s"} with changes`;

  return (
    <div className="pp-card" style={{ marginTop: 16 }}>
      <div className="pp-page-head" style={{ marginBottom: 8 }}>
        <h2 className="pp-page-title" style={{ fontSize: 18 }}>
          Medication history
        </h2>
        <div className="pp-page-sub">{subtitle}</div>
      </div>

      {allMeds.length === 0 ? (
        <p className="pp-detail-p">
          Once you add medicines, this is where you'll see which ones you took
          each day and how your list has changed over time.
        </p>
      ) : (
        <>
          <CalendarView
            allMeds={allMeds}
            eventsByDate={eventsByDate}
            selected={selected}
            onSelect={setSelected}
          />
          <DayDetail
            allMeds={allMeds}
            eventsByDate={eventsByDate}
            dateKey={selected}
            detailRef={detailRef}
          />
        </>
      )}
    </div>
  );
}
